import axios from 'axios';
import { baseUrl, endpoints } from './apis';
import LogOutUser from '../helper/logout';
import { Alert, Dimensions, Platform } from 'react-native';
import { getUniqueDeviceId, isCookieExpirated, isIframeCheck, trackEvent } from '@app/utils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { encrypt,decrypt,isEncrypted } from '@app/utils';
import {store} from '../store/newstore'
import { showErrorSheet } from '@app/store/slices/errorSlice';
import { AxiosRequestConfig } from "axios";

const encryptionSkipUrls = [
  endpoints?.bankInfoSearch
];

const shouldSkipEncryption = (url: string | undefined) => {
  if (!url) return false;
  return encryptionSkipUrls.some(skipPath => url.includes(skipPath));
};

// Extend AxiosRequestConfig to include our custom flag
declare module "axios" {
  export interface AxiosRequestConfig {
    handle500Here?: boolean;
  }
}
// const api = axios.create({
//   baseURL: baseUrl,
//   timeout: 60000,
// });

const InterceptorSetup = () => {
  // Request interceptor
  axios.interceptors.request.use(
    async req => {
      if ((req.method === 'post' || req.method === 'patch' || req.method === 'put') && req.data && isEncrypted(baseUrl) && !shouldSkipEncryption(req.url)) {
        req.data = {encryptedRequest:await encrypt(JSON.stringify(req.data))}; // Encrypt the request data
      }
      if(req.method === 'put'){
        req.url = req.url;
      }
      else {
        req.url = baseUrl + req.url;
      }
      req.headers['X-EntityType'] = 'BORROWER';
      req.headers['X-Appmode'] = 'INVESTOR_VIEW';
      req.headers['X-Deviceid'] =  Platform.OS === 'web' ? '' : await getUniqueDeviceId();
      req.headers['Content-Type'] = 'application/json';
      req.headers['Encryption'] =  isEncrypted(baseUrl);
      req.headers['X-DeviceType'] = Platform.OS === 'web' ? Dimensions.get('screen').width > 767 ? 'Desktop' :'MobileWeb':'Mobile';
      const appPlatform = store.getState().platformParams.platform;
      if(!req.headers['X-AppPlatform']){
        req.headers['X-AppPlatform'] = appPlatform ? appPlatform : 'VOLT_WEB_APP';
      }
      if(!req.headers['Authorization']){
        if(Platform.OS === 'web'){
          const isAccessTokenInCookiPreset = document.cookie.split(';').some((item) => item.trim().startsWith('access_token='));
          if(isAccessTokenInCookiPreset){
            const accessTokenCookie = document.cookie.split(';').find((item) => item.trim().startsWith('access_token='));
            const accessToken = accessTokenCookie?.split('=')[1];
            accessToken ? req.headers['Authorization'] = 'Bearer ' + accessToken: null
          }
          if(!isAccessTokenInCookiPreset && isIframeCheck()){
            const accessToken = await AsyncStorage.getItem('access_token');
            accessToken ? req.headers['Authorization'] = 'Bearer ' + accessToken: null
          }
        }
        else{
          const token = await AsyncStorage.getItem('access_token'); // get token from async storage
          token ? req.headers['Authorization'] = 'Bearer ' + token: null
        }
      }
      // Check if the platform is web and the user is offline
      if (Platform.OS === 'web' && !navigator?.onLine) {
        store.dispatch(
          showErrorSheet({
            isErrorSheetVisible: true,
            errorMessage: 'You are offline. Please check your internet connection.',
            closeOnBackdropPress: true,
            errorType: 'NO Internet',
            errorHeading: 'Network Error',
          })
        );

        // Block the request by rejecting the promise
        return Promise.reject(new Error('Network Error: Request blocked due to no internet connection.'));
      }
      return req;
    },
    err => {
      return Promise.reject(err);
    },
  );

  // Response interceptor
  axios.interceptors.response.use(
    async res => {
      if (isEncrypted(baseUrl) && !shouldSkipEncryption(res.config.url)) {
        const decryptedData = await decrypt(res?.data?.result); // DEncrypt the response data
        if(decryptedData!=="Logout successful" ){
          try {
            res.data = JSON.parse(decryptedData);
          } catch (error) {
            console.error("Failed to parse decrypted data:", error);
            trackEvent('FAILED_TO_PARSE_DECRYPTED_DATA_RES',{error:res.config.url})
            res.data = {};
          }
        }else{
          res.data = decryptedData; // DEncrypt the response data
        }// DEncrypt the response data
      }
      // Handle successful responses
      if (res.status === 200) {
        // console.log('Request successful');
      } else if (res.status === 201) {
        // console.log('Resource created successfully');
      }
      return res;
    },
   async  err => {
      let { response,status } = err;
      const config = err.config as AxiosRequestConfig;
      if (isEncrypted(baseUrl) && !shouldSkipEncryption(response?.config?.url)) {
        const decryptedData = await decrypt(response?.data?.result); // DEncrypt the response data
        if(decryptedData!=="Logout successful" ){
          try {
            response.data = JSON.parse(decryptedData);
          } catch (error) {
            console.error("Failed to parse decrypted data:", error);
            trackEvent('FAILED_TO_PARSE_DECRYPTED_DATA_ERR',{error:response?.config?.url})
            response.data = {}
          }
        }else{
          response.data = decryptedData; // DEncrypt the response data
        }
      }
      let isCookiesExpired =  false 

      if (Platform.OS === 'web') {
        isCookiesExpired = isCookieExpirated();
      }
      trackEvent('SHOW_ERROR_SHEET',{error:response?.config?.url})
      if(status===500){
        if (config?.handle500Here) {
          return Promise.reject(err);
        }

        store.dispatch(
          showErrorSheet({
            isErrorSheetVisible: true,
            errorMessage: 'Unable to process your request. Please try again after sometime.',
            closeOnBackdropPress: false,
            errorType: 'SERVER_ERROR',
            errorHeading: 'Something went wrong',
            btnName:'Ok, got it!'
          })
        );
        return Promise.reject(err);
      }
      if (response) {
        // Handle specific status codes
        if (response?.status === 401 || response?.status === 403 || isCookiesExpired ) {
          trackEvent('NEW_ARCH_INTERCEPTOR_LOGOUT_TRIGERRED',{error:JSON.stringify(response)})
          LogOutUser();
        } else if (response?.status === 400 || response?.status === 500) {
          console.error('Error message:', response.data);
        }
        return Promise.reject(response);
      } else {
        // Handle network or other errors

        const isNetworkError =
    err?.message?.includes('Network Error') ||
    err?.message?.includes('Network request failed') ||
    !err.response;

     if (isNetworkError && Platform.OS ==='ios') {
    Alert.alert(
      'No Internet Connection',
      'You seem to be offline. Please check your internet connection.',
      [
        {
          text: 'OK',
          onPress: () => {
          },
        },
      ],
      { cancelable: true }
    );
  }

        console.error('Network or other error:', err);
        return Promise.reject(err);
      }
    },
  );
};

// export { api };

export default InterceptorSetup;